"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { AddActivityOffcanvas } from "./add"
import { DeleteActivityModal } from "./delete"
import { EditActivityOffcanvas } from "./edit"
import { ActivitiesList } from "./list"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"
import toast from "react-hot-toast"

interface Project {
  id: number
  project_name: string
  status: string
  created_at: string
}

interface Activity {
  id: number
  activity_name: string
  activity_description: string
  site_completion_deadline_days?: number | null
  status: "ACTIVE" | "SUSPENDED"
  created_at: string
  updated_at: string
  created_by: number
  updated_by?: number
  suspended_by?: number
  suspended_at?: string
  projects?: Project[]
  project_count?: number
}

interface Pagination {
  offset: number
  limit: number
  page: number
  total_pages: number
  total_count: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

interface ActivityFormData {
  activity_name: string
  activity_description: string
  /** '' means "no deadline"; sent to the server as null. */
  site_completion_deadline_days?: string
  status?: "ACTIVE" | "SUSPENDED"
}

interface EditActivityFormData extends ActivityFormData {
  id: number
}

export const ActivitiesManagement: React.FC<{ onEditActivityOverride?: (activity: Activity) => void; hideAddActivityButton?: boolean }> = ({ onEditActivityOverride, hideAddActivityButton = false }) => {
  // State management
  const [activities, setActivities] = useState<Activity[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  })

  // Loading states
  const [activitiesLoading, setActivitiesLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  // Modal/Offcanvas states
  const [showAddActivity, setShowAddActivity] = useState(false)
  const [showEditActivity, setShowEditActivity] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null)

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>({
    field: 'created_at',
    direction: 'DESC'
  })

  // Debounced search — API fires only after 400 ms of inactivity
  const debouncedSearch = useDebounce(searchTerm, 400)

  // API Functions
  const fetchActivities = async (
    search = "",
    status = "",
    page = 1,
    limitValue?: number,
    sort = sortConfig
  ) => {
    setActivitiesLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const queryParams: any = {
        page: page,
        limit: effectiveLimit,
      }

      if (search.trim()) {
        queryParams.search = search
      }

      if (status.trim()) {
        queryParams.status = status
      }

      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }

      const response = await api.get("/activities/list", {
        params: queryParams,
      })

      if (response.data.success) {
        setActivities(response.data.data.activities || [])
        setPagination(response.data.data.pagination || pagination)
      } else {
        console.error("API returned success: false")
      }
    } catch (error) {
      console.error("Error fetching activities:", error)
    } finally {
      setActivitiesLoading(false)
    }
  }

  const fetchActivityById = async (activityId: number): Promise<Activity | null> => {
    try {
      const response = await api.get(`/activities/${activityId}`)

      if (response.data.success) {
        return response.data.data.activity
      }
      return null
    } catch (error) {
      console.error("Error fetching activity by ID:", error)
      return null
    }
  }

  const createActivity = async (activityData: ActivityFormData) => {
    setActionLoading(true)
    try {
      const response = await api.post("/activities/create", activityData)

      if (response.data.success) {
        await fetchActivities()
        setShowAddActivity(false)
        console.log("Activity created successfully")
      } else {
        console.error("Failed to create activity:", response.data.message)
      }
    } catch (error) {
      console.error("Error creating activity:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updateActivity = async (activityData: EditActivityFormData) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/activities/${activityData.id}`, {
        ...activityData,
        // The form holds the deadline as text so it can be cleared; the API
        // takes a number or null.
        site_completion_deadline_days:
          activityData.site_completion_deadline_days === undefined
            ? undefined
            : activityData.site_completion_deadline_days === ""
              ? null
              : Number(activityData.site_completion_deadline_days),
      })

      if (response.data.success) {
        await fetchActivities()
        setShowEditActivity(false)
        setSelectedActivity(null)
        console.log("Activity updated successfully")
      } else {
        console.error("Failed to update activity:", response.data.message)
      }
    } catch (error: any) {
      // A 403 here is the "Stop Survey Assignment to SE" gate on the deadline
      // days — worth surfacing rather than swallowing.
      console.error("Error updating activity:", error)
      toast.error(
        error?.response?.data?.message || "Failed to update activity",
      )
    } finally {
      setActionLoading(false)
    }
  }

  const deleteActivity = async () => {
    if (!selectedActivity) return

    setActionLoading(true)
    try {
      const response = await api.delete("/activities/delete", {
        data: { id: selectedActivity.id },
      })

      if (response.data.success) {
        await fetchActivities()
        setShowDeleteModal(false)
        setSelectedActivity(null)
        console.log("Activity deleted successfully")
      } else {
        console.error("Failed to delete activity:", response.data.message)
      }
    } catch (error) {
      console.error("Error deleting activity:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const toggleActivityStatus = async (activity: Activity, newStatus: "ACTIVE" | "SUSPENDED") => {
    setActionLoading(true)
    try {
      const response = await api.put(`/activities/toggle-status/${activity.id}`, {
        status: newStatus,
      })

      if (response.data.success) {
        await fetchActivities()
        console.log(`Activity ${newStatus.toLowerCase()} successfully`)
      } else {
        console.error("Failed to update activity status:", response.data.message)
      }
    } catch (error) {
      console.error("Error updating activity status:", error)
    } finally {
      setActionLoading(false)
    }
  }

  // Event handlers
  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // Actual API call is triggered by the debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchActivities(debouncedSearch, statusFilter, 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    fetchActivities(searchTerm, status, 1, undefined, sortConfig)
  }

  const handlePageChange = (page: number) => {
    fetchActivities(searchTerm, statusFilter, page, undefined, sortConfig)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchActivities(searchTerm, statusFilter, 1, limit, sortConfig)
  }

  const handleAddActivity = () => {
    setShowAddActivity(true)
  }

  const handleEditActivity = async (activity: Activity) => {
    if (onEditActivityOverride) {
      onEditActivityOverride(activity)
      return
    }
    const fullActivity = await fetchActivityById(activity.id)
    if (fullActivity) {
      setSelectedActivity(fullActivity)
      setShowEditActivity(true)
    }
  }

  const handleDeleteActivity = (activity: Activity) => {
    setSelectedActivity(activity)
    setShowDeleteModal(true)
  }

  const handleToggleStatus = (activity: Activity) => {
    const newStatus = activity.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE"
    toggleActivityStatus(activity, newStatus)
  }

  const handleCloseModals = () => {
    setShowAddActivity(false)
    setShowEditActivity(false)
    setShowDeleteModal(false)
    setSelectedActivity(null)
  }

  // Initialize data on component mount
  useEffect(() => {
    fetchActivities()
  }, [])

  const handleSort = (field: string, direction: 'ASC' | 'DESC') => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchActivities(searchTerm, statusFilter, pagination.page, undefined, sort)
  }

  return (
    <div className="">
      <div className="">
        {/* Main Activities List */}
        <ActivitiesList
          activities={activities}
          pagination={pagination}
          onSearch={handleSearch}
          onStatusFilter={handleStatusFilter}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onAddActivity={handleAddActivity}
          onEditActivity={handleEditActivity}
          onDeleteActivity={handleDeleteActivity}
          onToggleStatus={handleToggleStatus}
          onSort={handleSort}
          loading={activitiesLoading}
          searchValue={searchTerm}
          statusValue={statusFilter}
          sortState={sortConfig}
          hideAddActivityButton={hideAddActivityButton}
        />

        {/* Add Activity Offcanvas */}
        <AddActivityOffcanvas
          isOpen={showAddActivity}
          onClose={handleCloseModals}
          onSubmit={createActivity}
          loading={actionLoading}
        />

        {/* Edit Activity Offcanvas */}
        <EditActivityOffcanvas
          isOpen={showEditActivity}
          onClose={handleCloseModals}
          onSubmit={updateActivity}
          activity={selectedActivity}
          loading={actionLoading}
        />

        {/* Delete Activity Modal */}
        <DeleteActivityModal
          isOpen={showDeleteModal}
          onClose={handleCloseModals}
          onDelete={deleteActivity}
          text={
            selectedActivity
              ? `Are you sure you want to delete the activity "${selectedActivity.activity_name}"? This will permanently remove the activity and may affect associated projects.`
              : ""
          }
          loading={actionLoading}
        />
      </div>
    </div>
  )
}
